import React, { useEffect, useRef, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { ChatCircle, PaperPlaneTilt, X } from "@phosphor-icons/react";

function useSessionId() {
    const [id] = useState(() => {
        const existing = localStorage.getItem("br_chat_sid");
        if (existing) return existing;
        const nv = `chat-${Math.random().toString(36).slice(2)}-${Date.now()}`;
        localStorage.setItem("br_chat_sid", nv);
        return nv;
    });
    return id;
}

export default function Chatbot() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([
        { from: "bot", text: "Hello — I'm your Butler concierge. How can I help today?" },
    ]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const sessionId = useSessionId();
    const listRef = useRef(null);

    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, [messages, open]);

    async function send() {
        const msg = input.trim();
        if (!msg) return;
        setInput("");
        setMessages((m) => [...m, { from: "user", text: msg }]);
        setSending(true);
        try {
            const { data } = await http.post("/chat", { session_id: sessionId, message: msg });
            setMessages((m) => [...m, { from: "bot", text: data.reply }]);
        } catch (e) {
            setMessages((m) => [...m, { from: "bot", text: formatApiError(e) }]);
        } finally {
            setSending(false);
        }
    }

    return (
        <div className="fixed bottom-5 right-5 z-50">
            {open ? (
                <div
                    data-testid="chat-panel"
                    className="w-[340px] sm:w-[380px] h-[480px] bg-slate-900 border border-white/10 rounded-sm shadow-2xl flex flex-col"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                        <div>
                            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Concierge</div>
                            <div className="font-serif text-lg gold-text">Butler AI</div>
                        </div>
                        <button data-testid="chat-close" onClick={() => setOpen(false)} className="text-slate-400 hover:text-white">
                            <X size={20} />
                        </button>
                    </div>
                    <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-sm">
                        {messages.map((m, i) => (
                            <div key={i} className={m.from === "user" ? "text-right" : ""}>
                                <span
                                    className={`inline-block max-w-[85%] px-3 py-2 rounded-sm ${m.from === "user"
                                        ? "bg-gold text-slate-950"
                                        : "bg-slate-800/80 text-slate-100 border border-white/5"}`}
                                >
                                    {m.text}
                                </span>
                            </div>
                        ))}
                        {sending && <div className="text-slate-500 text-xs italic">Butler is typing…</div>}
                    </div>
                    <div className="border-t border-white/10 p-3 flex items-center gap-2">
                        <input
                            data-testid="chat-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && send()}
                            placeholder="Ask about a service…"
                            className="flex-1 bg-slate-950 border border-white/10 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-gold"
                        />
                        <button
                            data-testid="chat-send"
                            onClick={send}
                            disabled={sending}
                            className="btn-gold text-sm px-3 py-2 disabled:opacity-50"
                        >
                            <PaperPlaneTilt size={16} weight="fill" />
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    data-testid="chat-open"
                    onClick={() => setOpen(true)}
                    className="bg-gold text-slate-950 rounded-full w-14 h-14 flex items-center justify-center shadow-xl hover:bg-[#F3E5AB] transition"
                >
                    <ChatCircle size={26} weight="fill" />
                </button>
            )}
        </div>
    );
}
